/**
 * An asset's detail page (F5).
 *
 * Read-only, for the same reason the customer page is: editing eleven flags,
 * a grid connection and a coordinate pair needs a real form, and the record is
 * more useful readable now than editable later. Pilots link here.
 *
 * The pilot-ready verdict leads, because it is the question a reader opens this
 * page with — and the two flag checklists below it are the *reasons* for that
 * verdict, so they sit together rather than in separate tabs.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.4, PRD §5.6
 */
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { BLOCKER_FLAGS, SUITABILITY_FLAGS } from '../../components/domain/asset-taxonomy';
import { AssetStatusBadge } from '../../components/domain/AssetStatusBadge';
import { Capacity } from '../../components/domain/Capacity';
import { DefinitionList } from '../../components/domain/DefinitionList';
import { Badge } from '../../components/ui/Badge';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Gate } from '../../components/ui/Gate';
import { Grid, Stack } from '../../components/ui/Grid';
import { Kicker } from '../../components/ui/Kicker';
import { Note } from '../../components/ui/Note';
import { PageHead } from '../../components/ui/PageHead';
import { formatDate, formatNumber } from '../../lib/format';
import { ROUTES } from '../../lib/routes';

import { useAsset } from './api/assets.queries';

export function AssetDetailPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();

  const { data, isPending, isError } = useAsset(id);

  if (isPending) {
    return (
      <p role="status" className="text-body">
        {t('state.loading')}
      </p>
    );
  }

  if (isError) {
    return (
      <div>
        <p role="alert" className="text-sem-bad mb-s2">
          {t('asset.notFound')}
        </p>
        <Link to={ROUTES.assets} className="text-blue text-sm underline">
          {t('asset.backToList')}
        </Link>
      </div>
    );
  }

  const notSet = <span className="text-gray-400">{t('startup.notSet')}</span>;

  /**
   * A megawatt figure with its unit, or a marker.
   *
   * Three decimal places, matching the `Decimal(12,3)` column. `formatNumber`
   * rounds to whole numbers by default, which turned an 18.75 MW connection
   * limit into "19 MW" — and a grid limit is the one figure on this page that
   * a pilot's output gets compared against, so rounding it up is the wrong
   * direction to be wrong in.
   */
  const mw = (value: number | null): React.ReactNode =>
    value === null ? (
      notSet
    ) : (
      <span className="tabular-nums">
        {t('asset.fields.mwValue', {
          value: formatNumber(value, { maximumFractionDigits: 3 }),
        })}
      </span>
    );

  return (
    <>
      <nav aria-label={t('startup.breadcrumb')} className="text-body mb-s2 text-xs">
        {/* Persistently underlined, not only on hover: inside a text block a
            link distinguished by colour alone fails WCAG 1.4.1 (PRD §9.7). */}
        <Link to={ROUTES.assets} className="text-blue underline">
          {t('asset.title')}
        </Link>
        <span className="text-gray-200 mx-1.5">/</span>
        <span>{data.name}</span>
      </nav>

      <PageHead
        title={data.name}
        lead={
          data.customerName === null
            ? t(`asset.type.${data.type}`)
            : t('asset.leadWithCustomer', {
                type: t(`asset.type.${data.type}`),
                customer: data.customerName,
              })
        }
        actions={
          <>
            <AssetStatusBadge status={data.status} />
            {data.isPilotReady ? (
              <Badge tone="ok">{t('asset.pilotReady')}</Badge>
            ) : data.blockerCount > 0 ? (
              <Badge tone="warn">
                {t('asset.columns.blockerCount', { count: data.blockerCount })}
              </Badge>
            ) : null}
          </>
        }
      />

      <Grid cols="detail">
        <Stack>
          {/* The reasons for the verdict in the header, in the order PRD §5.6
              lists them. Suitability first: what the site HAS is the starting
              point, and the blockers are what stands in the way of using it. */}
          <Card>
            <CardHead>
              <Kicker as="h2">
                {t('asset.sections.suitability', {
                  count: data.suitabilityCount,
                  total: SUITABILITY_FLAGS.length,
                })}
              </Kicker>
            </CardHead>
            <CardBody>
              {SUITABILITY_FLAGS.map((flag) => (
                <Gate key={flag} done={data[flag]}>
                  {t(`asset.suitability.${flag}`)}
                </Gate>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHead>
              <Kicker as="h2">
                {t('asset.sections.blockers', { count: data.blockerCount })}
              </Kicker>
            </CardHead>
            <CardBody>
              {/* Inverted deliberately: a blocker that is SET is a problem, so
                  `done` means "clear". Reading a tick as good keeps both
                  checklists scannable the same way. */}
              {BLOCKER_FLAGS.map((flag) => (
                <Gate key={flag} done={!data[flag]}>
                  {t(`asset.blockers.${flag}`)}
                </Gate>
              ))}

              {data.blockerCount === 0 ? null : (
                <Note className="mt-s3">{t('asset.blockersNote')}</Note>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHead>
              <Kicker as="h2">{t('asset.sections.grid')}</Kicker>
            </CardHead>
            <CardBody>
              <DefinitionList
                items={[
                  {
                    label: t('asset.fields.mic'),
                    value: mw(data.gridConnectionMic),
                  },
                  {
                    label: t('asset.fields.mec'),
                    value: mw(data.gridConnectionMec),
                  },
                  {
                    label: t('asset.fields.voltage'),
                    value: data.voltageLevel ?? notSet,
                  },
                  {
                    label: t('asset.fields.operator'),
                    value: data.networkOperator ?? notSet,
                  },
                ]}
              />
            </CardBody>
          </Card>
        </Stack>

        <Stack>
          <Card>
            <CardHead>
              <Kicker as="h2">{t('asset.sections.site')}</Kicker>
            </CardHead>
            <CardBody>
              <DefinitionList
                items={[
                  {
                    label: t('asset.fields.capacity'),
                    /* `table` precision, which is the THREE-significant-figure
                       setting — `headline` is the coarser one, for a single
                       large figure a reader is meant to remember. A site's
                       rating wants the finer of the two. */
                    value:
                      data.capacityMw === null && data.capacityMwh === null ? (
                        notSet
                      ) : (
                        <Capacity mw={data.capacityMw} mwh={data.capacityMwh} />
                      ),
                  },
                  {
                    label: t('asset.fields.ownership'),
                    value: t(`asset.ownership.${data.ownership}`),
                  },
                  {
                    label: t('asset.fields.location'),
                    value: data.location ?? notSet,
                  },
                  {
                    label: t('asset.fields.country'),
                    value: data.country ?? notSet,
                  },
                  {
                    label: t('asset.fields.commissioned'),
                    value:
                      data.commissioningDate === null
                        ? notSet
                        : formatDate(data.commissioningDate),
                  },
                ]}
              />
            </CardBody>
          </Card>

          {/* Whether a pilot can be measured at all. A site with no metering
              and no remote access cannot produce a KPI actual, which is what
              PRD §5.4 makes a pilot's whole point. */}
          <Card>
            <CardHead>
              <Kicker as="h2">{t('asset.sections.data')}</Kicker>
            </CardHead>
            <CardBody>
              <Gate done={data.hasHalfHourlyMetering}>
                {t('asset.fields.halfHourly')}
              </Gate>
              <Gate done={data.hasRemoteAccess}>{t('asset.fields.remoteAccess')}</Gate>
              <DefinitionList
                className="mt-s2"
                items={[
                  {
                    label: t('asset.fields.scada'),
                    value: data.scadaEmsVendor ?? notSet,
                  },
                ]}
              />
            </CardBody>
          </Card>

          <Note>{t('asset.readOnlyNote')}</Note>
        </Stack>
      </Grid>
    </>
  );
}
